import React, { useMemo, useState, useEffect } from 'react';
import type { FunctionComponent } from 'react';
import type { IProject, IProjects, Translation } from '../../interface/Localization';
import type { AxiosError } from 'axios';
import { Text, Button, useToast } from '@chakra-ui/react';
import { TableContainer } from '../../components/table/TableContainer';
import type { Column } from 'react-table';
import { FormComponent } from './FormComponent';
import { CreateTranslationModal } from './CreateTranslationModal';
import { useParams } from 'react-router';
import { Request as apiRequest } from '../../helper';
import { AddIcon } from '@chakra-ui/icons';

interface Props {
    translationsData: Record<string, Translation[]>;
    searchQuery: boolean;
    onDataRefresh?: () => void | Promise<void>;
}

interface CreateLocalizationKeyPayload {
    name: string;
    values?: Array<{
        language: string;
        value: string;
    }>;
}

const buildCreateLocalizationKeyPayload = (name: string, drafts: Translation[]): CreateLocalizationKeyPayload => {
    const valuesByLanguage = new Map<string, string>();

    drafts.forEach((draft) => {
        const language = draft.language?.trim();
        const value = draft.value ?? '';

        if (!language || !value.trim() || valuesByLanguage.has(language)) {
            return;
        }

        valuesByLanguage.set(language, value);
    });

    const values = Array.from(valuesByLanguage.entries()).map(([language, value]) => ({
        language,
        value,
    }));

    return {
        name: name.trim(),
        ...(values.length > 0 ? { values } : {}),
    };
};

const toastCreateKeyError = (toast: ReturnType<typeof useToast>, error: unknown): void => {
    const status = (error as AxiosError)?.response?.status;

    if (status === 404) {
        toast({
            title: 'Project not found.',
            status: 'error',
            duration: 3000,
            isClosable: true,
        });
        return;
    }

    if (status === 409) {
        toast({
            title: 'Translation key already exists.',
            status: 'error',
            duration: 3000,
            isClosable: true,
        });
        return;
    }

    if (status === 400) {
        toast({
            title: 'Invalid key payload or unknown language.',
            status: 'error',
            duration: 3000,
            isClosable: true,
        });
        return;
    }

    toast({
        title: 'Error creating translation.',
        status: 'error',
        duration: 3000,
        isClosable: true,
    });
};

export const LocalizationsTableComponent: FunctionComponent<Props> = (props) => {
    const { translationsData, searchQuery, onDataRefresh } = props;
    const { projectId } = useParams();
    const toast = useToast();
    const [isCreateTranslationDialogOpen, setIsCreateTranslationDialogOpen] = useState<boolean>(false);
    const [newTranslationDrafts, setNewTranslationDrafts] = useState<Translation[]>([]);
    const [newTranslationKey, setNewTranslationKey] = useState<string>('');
    const [selectedProjectId, setSelectedProjectId] = useState<string>('');
    const [isSavingNewTranslation, setIsSavingNewTranslation] = useState<boolean>(false);
    const [projectNameById, setProjectNameById] = useState<Record<string, string>>({});

    const tableData = useMemo(
        () =>
            translationsData
                ? Object.entries(translationsData).map(
                      ([key, translations]) =>
                          [
                              key,
                              [...translations].sort((a, b) =>
                                  (a.language_code ?? '').localeCompare(b.language_code ?? ''),
                              ),
                          ] as [string, typeof translations],
                  )
                : undefined,
        [translationsData],
    );

    useEffect(() => {
        let isMounted = true;

        const loadProjectNames = async (): Promise<void> => {
            try {
                const response = await apiRequest.get<IProjects>({ path: '/localization/project' });
                if (!isMounted) {
                    return;
                }

                const names: Record<string, string> = {};
                response.data.result.forEach((project) => {
                    names[project.id] = project.name;
                });
                setProjectNameById(names);
            } catch {
                // Keep existing labels when loading names fails.
            }
        };

        loadProjectNames();

        return () => {
            isMounted = false;
        };
    }, []);

    const projectOptions = useMemo(() => {
        const projectMap = new Map<string, string>();
        Object.values(translationsData ?? {}).forEach((group) => {
            group.forEach((translation) => {
                const derivedProjectId = (translation.project_id ?? projectId ?? '').trim();
                if (!derivedProjectId) {
                    return;
                }
                if (!projectMap.has(derivedProjectId)) {
                    projectMap.set(
                        derivedProjectId,
                        translation.project?.trim() ?? projectNameById[derivedProjectId] ?? 'Unknown project',
                    );
                }
            });
        });

        if (projectMap.size === 0 && projectId) {
            projectMap.set(projectId, projectNameById[projectId] ?? 'Current project');
        }

        return Array.from(projectMap.entries()).map(([id, name]) => ({ id, name }));
    }, [translationsData, projectId, projectNameById]);

    const translationTemplateByProject = useMemo(() => {
        const templates = new Map<string, Translation[]>();

        Object.values(translationsData ?? {}).forEach((group) => {
            const firstTranslation = group[0];
            if (!firstTranslation) {
                return;
            }

            const derivedProjectId = (firstTranslation.project_id ?? projectId ?? '').trim();
            if (!derivedProjectId || templates.has(derivedProjectId)) {
                return;
            }

            templates.set(derivedProjectId, group);
        });

        if (templates.size === 0 && projectId) {
            const firstEntry = Object.values(translationsData ?? {})[0];
            if (firstEntry) {
                templates.set(projectId, firstEntry);
            }
        }

        return templates;
    }, [translationsData, projectId]);

    useEffect(() => {
        if (projectOptions.length === 0) {
            setSelectedProjectId('');
            return;
        }

        if (selectedProjectId && projectOptions.some((project) => project.id === selectedProjectId)) {
            return;
        }

        const kernelProjectId = projectOptions.find((project) => project.name.trim().toLowerCase() === 'kernel')?.id;
        const preferredProject =
            kernelProjectId ??
            (projectId && projectOptions.some((project) => project.id === projectId)
                ? projectId
                : projectOptions[0].id);
        setSelectedProjectId(preferredProject);
    }, [projectOptions, projectId, selectedProjectId]);

    const getDraftsForProject = (targetProjectId: string): Translation[] => {
        const translationTemplate = translationTemplateByProject.get(targetProjectId);
        const projectName = projectOptions.find((project) => project.id === targetProjectId)?.name;

        if (!translationTemplate) {
            return [];
        }

        return translationTemplate.map((translation) => ({
            ...translation,
            id: '',
            value_id: '',
            key: '',
            value: '',
            placeholder: '',
            project_id: targetProjectId,
            project: projectName,
        }));
    };

    const openCreateTranslationDialog = (): void => {
        if (!selectedProjectId) {
            return;
        }

        setNewTranslationKey('');
        setNewTranslationDrafts(getDraftsForProject(selectedProjectId));
        setIsCreateTranslationDialogOpen(true);
    };

    const closeCreateTranslationDialog = (): void => {
        setIsCreateTranslationDialogOpen(false);
        setNewTranslationDrafts([]);
        setNewTranslationKey('');
    };

    const handleNewTranslationValueChange = (language: string, value: string): void => {
        setNewTranslationDrafts((prevData) =>
            prevData.map((translation) =>
                translation.language === language ? { ...translation, value, placeholder: value } : translation,
            ),
        );
    };

    const handleNewTranslationProjectChange = (nextProjectId: string): void => {
        setSelectedProjectId(nextProjectId);
        setNewTranslationDrafts(getDraftsForProject(nextProjectId));
    };

    const saveNewTranslations = async (): Promise<void> => {
        if (newTranslationDrafts.length === 0 || !newTranslationKey.trim() || !selectedProjectId.trim()) {
            return;
        }

        setIsSavingNewTranslation(true);

        try {
            await apiRequest.post<IProject>({
                path: '/localization/project/' + selectedProjectId + '/key',
                data: buildCreateLocalizationKeyPayload(newTranslationKey, newTranslationDrafts),
            });

            toast({
                title: 'Translation created.',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });

            closeCreateTranslationDialog();
            if (onDataRefresh) {
                await onDataRefresh();
            }
        } catch (error) {
            toastCreateKeyError(toast, error);
        } finally {
            setIsSavingNewTranslation(false);
        }
    };

    let columns: Array<Column<Record<string, Translation[]>>> = useMemo(
        () => [
            {
                Header: 'Localization',
                accessor: (record) => record[1][0].key,
                id: 'Localization',
                Cell: (cell: any) => {
                    const resolvedProjectId = projectId ?? cell.row.original[1][0]?.project_id ?? '';
                    return (
                        <FormComponent
                            translationData={cell.row.original[1]}
                            projectId={resolvedProjectId}
                            onVariationCreated={onDataRefresh}
                        />
                    );
                },
            },
            {
                Header: 'Updated at',
                accessor: (record) => record[1][0].updated_at,
                minWidth: 60,
                maxWidth: 60,
                Cell: (cell: any) => {
                    return (
                        <>
                            {cell.row.original[1].map((trans: Translation, index: number) => (
                                <Text
                                    key={trans.id + '/' + trans.language}
                                    marginBottom={2}
                                    height={10}
                                    // Adding margin to the first updated_at to align with the values of the translation
                                    marginTop={index === 0 ? 18 : undefined}
                                >
                                    {trans.updated_at}
                                </Text>
                            ))}
                        </>
                    );
                },
            },
        ],
        [onDataRefresh, projectId, translationsData],
    );

    const button: Array<Column<Record<string, Translation[]>>> = useMemo(
        () => [
            {
                Header: 'state',
                accessor: (record) => {
                    const translations = record[1];
                    if (translations.every((t: any) => t.state === StateType.Finished)) return StateType.Finished;
                    if (translations.every((t: any) => t.state === StateType.InProgress)) return StateType.NotStarted;
                    return StateType.InProgress;
                },
            },
            {
                Header: 'Save',
                accessor: 'button',
            },
            {
                Header: 'Placeholder',
                accessor: 'placeholder',
            },
            {
                Header: 'searchfield',
                accessor: (record: any) => {
                    return record[1].map((trans: Translation) => trans.value).join(' ');
                },
            },
        ],
        [translationsData],
    );

    columns = columns.concat(button);

    let localizationSearchConfig;
    if (!searchQuery) {
        localizationSearchConfig = {
            placeholder: 'Search for localization',
            selectConfig: {
                columAccessor: 'state',
                selectOptions: <StateOptions />,
            },
        };
    }

    return tableData ? (
        <>
            <TableContainer
                key="localizationsTable"
                columns={columns}
                data={tableData}
                toolbarRightContent={
                    !searchQuery ? (
                        <Button leftIcon={<AddIcon />} colorScheme="blue" onClick={openCreateTranslationDialog}>
                            Create new translation
                        </Button>
                    ) : undefined
                }
                search={localizationSearchConfig}
                hiddenColumns={['placeholder', 'state', 'searchfield', 'button']}
                showColoredState={true}
                coloredStateMethod={colorForState}
                initialState={{
                    sortBy: [{ id: 'Localization', desc: false }],
                }}
                defaultPageSize={20}
            />
            <CreateTranslationModal
                isOpen={isCreateTranslationDialogOpen}
                onClose={closeCreateTranslationDialog}
                projectOptions={projectOptions}
                selectedProjectId={selectedProjectId}
                onProjectChange={handleNewTranslationProjectChange}
                newTranslationKey={newTranslationKey}
                onKeyChange={setNewTranslationKey}
                newTranslationDrafts={newTranslationDrafts}
                onValueChange={handleNewTranslationValueChange}
                onSave={saveNewTranslations}
                isSaving={isSavingNewTranslation}
            />
        </>
    ) : (
        <></>
    );
};

enum StateType {
    NotStarted = 0,
    InProgress = 1,
    Finished = 2,
}

const allStates = [StateType.NotStarted, StateType.InProgress, StateType.Finished];

const colorForState = (state: StateType): string => {
    switch (state) {
        case StateType.NotStarted:
            return 'red.500';
        case StateType.InProgress:
            return 'blue.500';
        case StateType.Finished:
            return 'green.500';
    }
};

const labelForState = (state: StateType): string => {
    switch (state) {
        case StateType.NotStarted:
            return 'Not Started';
        case StateType.InProgress:
            return 'In Progress';
        case StateType.Finished:
            return 'Finished';
    }
};

const StateOptions: FunctionComponent = () => {
    return (
        <>
            <option value="">All</option>
            {allStates.map((state: StateType) => (
                <option value={state} key={state}>
                    {labelForState(state)}
                </option>
            ))}
        </>
    );
};
